/**
 * @file new-request-form.tsx
 * @module app/modules/service-request/components/new-request-form
 * @description Form for raising a service request: pick one of the actions the user's role
 * can request, fill in that action's inputs, give a reason, and submit for approval. Inputs come
 * from the action's field definitions, so a new action or input kind needs no change here.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { FormEvent, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Info } from 'lucide-react';
import { ScrollableTabs } from '../../_common/components/scrollable-tabs';
import {
  emptyValuesFor,
  FieldErrors,
  RequestFieldsEditor,
  validateFields,
} from './fields/request-fields';
import {
  CreateServiceRequestPayload,
  ServiceRequestActionDefinition,
  ServiceRequestPayload,
} from '../types/service-request.types';
import { MAX_NOTE_LENGTH } from '../utils/service-request.constants';

type NewRequestFormProps = {
  /** Actions the user's role can request, from the server. */
  actions: ServiceRequestActionDefinition[];
  onCancel: () => void;
  onSubmit: (body: CreateServiceRequestPayload) => Promise<void>;
};

const NewRequestForm = ({
  actions,
  onCancel,
  onSubmit,
}: NewRequestFormProps) => {
  const [actionKey, setActionKey] = useState(actions[0]?.key || '');
  const action = actions.find((item) => item.key === actionKey) || actions[0];
  // Typed values belong to one action; switching action (or a new catalog) starts empty.
  const [draft, setDraft] = useState<{
    key: string;
    values: ServiceRequestPayload;
  } | null>(null);
  const [note, setNote] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  if (!action) {
    return (
      <div className="sr-form">
        <div className="sr-callout sr-callout--info">
          <Info size={18} aria-hidden="true" />
          <span>
            Your role cannot raise any request yet. An admin can allow it under
            Settings &gt; General &gt; Service Request.
          </span>
        </div>
        <div className="sr-form__actions">
          <button
            type="button"
            className="uw-btn uw-btn-light btn btn-light"
            onClick={onCancel}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const values =
    draft?.key === action.key ? draft.values : emptyValuesFor(action);
  const fieldErrors: FieldErrors = attempted
    ? validateFields(action, values)
    : {};
  const noteError =
    attempted && !note.trim()
      ? 'Tell the approver why this request is needed.'
      : '';

  const chooseAction = (next: ServiceRequestActionDefinition) => {
    if (next.key === action.key) return;
    setActionKey(next.key);
    setDraft(null);
    setAttempted(false);
    setServerError('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    setServerError('');
    const errors = validateFields(action, values);
    if (Object.keys(errors).length || !note.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        action: action.key,
        input: values,
        requestReason: note.trim(),
      });
    } catch (submitError) {
      setServerError(
        submitError instanceof Error && submitError.message
          ? submitError.message
          : 'Unable to submit the request. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <form className="sr-form" onSubmit={handleSubmit} noValidate>
      <div>
        <div className="sr-form__label">Request type</div>
        <ScrollableTabs
          tabs={actions}
          activeKey={action.key}
          onSelect={(next) => {
            if (!submitting) chooseAction(next);
          }}
        />
      </div>

      <div className="sr-callout sr-callout--info">
        <Info size={18} aria-hidden="true" />
        <span>{action.description}</span>
      </div>

      <RequestFieldsEditor
        action={action}
        values={values}
        errors={fieldErrors}
        disabled={submitting}
        onChange={(name, value) =>
          setDraft((current) => ({
            key: action.key,
            values: {
              ...(current?.key === action.key
                ? current.values
                : emptyValuesFor(action)),
              [name]: value,
            },
          }))
        }
      />

      <div className="sr-form__section">
        <label className="sr-form__label" htmlFor="sr-note">
          Reason <span className="text-danger">*</span>
        </label>
        <textarea
          id="sr-note"
          className={`form-control sr-textarea${
            noteError ? ' is-invalid' : ''
          }`}
          rows={3}
          maxLength={MAX_NOTE_LENGTH}
          value={note}
          disabled={submitting}
          placeholder="For example: customer called to cancel, duplicate checkout."
          aria-invalid={Boolean(noteError) || undefined}
          aria-describedby={noteError ? 'sr-note-error' : 'sr-note-hint'}
          onChange={(event) => setNote(event.target.value)}
        />
        {noteError ? (
          <div id="sr-note-error" className="sr-field-error">
            {noteError}
          </div>
        ) : (
          <div id="sr-note-hint" className="sr-field-hint">
            <span>The approver sees this reason.</span>
            <span>
              {note.length} / {MAX_NOTE_LENGTH}
            </span>
          </div>
        )}
      </div>

      {serverError ? (
        <div className="sr-callout sr-callout--danger" role="alert">
          {serverError}
        </div>
      ) : null}

      <div className="sr-form__actions">
        <button
          type="button"
          className="uw-btn uw-btn-light btn btn-light"
          disabled={submitting}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="uw-btn uw-btn-primary btn btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2"
                aria-hidden="true"
              />
              Submitting...
            </>
          ) : (
            'Submit for approval'
          )}
        </button>
      </div>
    </form>
  );
};

export default NewRequestForm;
